import { Field, Label } from '@headlessui/react';
import { FC } from 'react';

import { useFormat } from '../../../hooks/index';
import { SharePlanVestingType } from '../../../types/pool-plans.types';
import {
  ApproveMilestoneDto,
  TargetBasedVestingMilestoneItem,
  VestingTask,
} from '../../../types/vesting-tasks.types';
import { calcMilestoneIssuedShares } from '../../../utils/calc-milestone-issued-shares.util';
import { Checkbox } from '../../checkbox/index';
import { DatePicker } from '../../date-picker/index';
import { TableRow } from '../../table/index';
import { ApproveTargetBasedColumn, approveTargetBasedColumnMap } from './types';

export type TargetBasedTableRowProps = {
  isSelected: boolean;
  milestone: TargetBasedVestingMilestoneItem | undefined;
  milestoneIndex: number;
  milestones: TargetBasedVestingMilestoneItem[];
  onRemoveSelect: (stakeholderId: string) => void;
  onSelect: (data: Except<ApproveMilestoneDto, 'milestoneIndex'>) => void;
} & VestingTask;

export const TargetBasedTableRow: FC<TargetBasedTableRowProps> = ({
  isSelected,
  onRemoveSelect,
  onSelect,
  id,
  milestone,
  milestoneIndex,
  milestones,
  totalSharesCount,
  grantItem: {
    id: grantId,
    stakeholder: { fullName },
    plan: {
      name,
      vesting,
      pool: { shareClass },
    },
  },
}) => {
  const { format } = useFormat();
  if (vesting?.type !== SharePlanVestingType.TARGET_BASED) return <></>;

  const issuedShares = calcMilestoneIssuedShares({
    milestoneIndex,
    milestones,
    totalSharesCount,
  });

  return (
    <TableRow
      columnOrder={approveTargetBasedColumnMap}
      contentClassName="pl-4"
      fields={{
        id,
        [ApproveTargetBasedColumn.SELECT]: (
          <Field className="flex h-full w-full items-center">
            <Checkbox
              checked={isSelected}
              id={id}
              onChange={(checked) => {
                checked
                  ? onSelect({
                      vestingTaskId: id,
                      value: issuedShares,
                    })
                  : onRemoveSelect(id);
              }}
            />
            <Label className="absolute left-0 top-0 h-full w-full cursor-pointer" htmlFor={id} />
          </Field>
        ),
        [ApproveTargetBasedColumn.STAKEHOLDER]: fullName,
        [ApproveTargetBasedColumn.GRANT]: grantId.split('-')[0].toUpperCase(),
        [ApproveTargetBasedColumn.APPROVAL_DATE]: (
          <DatePicker className="border-b border-gray-700 bg-gray-50 px-3 py-2" />
        ),
        [ApproveTargetBasedColumn.SHARE_CLASS]: shareClass.name,
        [ApproveTargetBasedColumn.TARGET_DATE]: format(milestone?.targetDate, 'dd/MM/yyyy'),
        [ApproveTargetBasedColumn.TARGET_NAME]: milestone?.name,
        [ApproveTargetBasedColumn.PLAN_TYPE]: name,
        [ApproveTargetBasedColumn.ISSUED_SHARES]: issuedShares,
      }}
      trClassName={isSelected && 'bg-gray-50'}
    />
  );
};
